import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import offlineService from '../services/offline';

const NetworkStatus = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      const count = await offlineService.getQueueCount();
      setQueueCount(count);
    };

    // Listen for status changes
    const unsubscribe = offlineService.onStatusChange((event, data) => {
      if (event === 'status') {
        setOnline(data);
        setShow(true);
        setTimeout(() => setShow(false), 3000);
        if (data) checkStatus();
      }
      if (event === 'queued') {
        checkStatus();
        setShow(true);
      }
      if (event === 'synced') {
        checkStatus();
        setSyncing(false);
        setShow(true);
        setTimeout(() => setShow(false), 3000);
      }
    });

    // Initial check
    checkStatus();

    // Online/offline events
    const goOnline = async () => {
      setOnline(true);
      setShow(true);
      setTimeout(() => setShow(false), 3000);
      // Auto sync
      setSyncing(true);
      await offlineService.syncQueue();
      setSyncing(false);
      checkStatus();
    };
    const goOffline = () => {
      setOnline(false);
      setShow(true);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    const result = await offlineService.syncQueue();
    setSyncing(false);
    const count = await offlineService.getQueueCount();
    setQueueCount(count);
    if (result.synced > 0) {
      // Reload data setelah sync
      window.location.reload();
    }
  };

  if (!show && online && queueCount === 0) return null;

  return (
    <div className={`fixed bottom-4 right-4 z-50 transition-all duration-500 animate-slide-up`}>
      <div className={`flex items-center space-x-2 px-3 py-2 rounded-xl shadow-lg backdrop-blur-sm border ${
        !online
          ? 'bg-red-50 border-red-200 text-red-700'
          : syncing
            ? 'bg-blue-50 border-blue-200 text-blue-700'
            : queueCount > 0
              ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
              : 'bg-green-50 border-green-200 text-green-700'
      }`}>
        {!online ? (
          <>
            <WifiOff className="w-4 h-4" />
            <span className="text-xs font-semibold">Offline</span>
            {queueCount > 0 && (
              <span className="text-[10px] font-medium bg-red-100 px-1.5 py-0.5 rounded-full">
                {queueCount} pending
              </span>
            )}
          </>
        ) : syncing ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-xs font-semibold">Menyinkronkan...</span>
          </>
        ) : queueCount > 0 ? (
          <>
            <Wifi className="w-4 h-4" />
            <span className="text-xs font-semibold">{queueCount} data menunggu sync</span>
            <button
              onClick={handleSync}
              className="text-[10px] font-bold bg-yellow-100 px-2 py-0.5 rounded-full hover:bg-yellow-200 transition-colors"
            >
              Sync
            </button>
          </>
        ) : (
          <>
            <Wifi className="w-4 h-4" />
            <span className="text-xs font-semibold">Online</span>
          </>
        )}
      </div>
    </div>
  );
};

export default NetworkStatus;
